import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterForm({ source = "insights", compact = false }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email, source });
      setDone(true);
      toast.success("Subscribed. Check your inbox.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not subscribe. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className={`rounded-2xl border ${compact ? "p-5" : "p-6"} bg-emerald-50 border-emerald-200 flex items-center gap-3`}
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0" />
        <div>
          <div className="font-semibold text-emerald-900 text-[15px]">You&apos;re on the list.</div>
          <div className="text-emerald-800 text-[13px] mt-0.5">A welcome note is on its way to your inbox.</div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="newsletter-form"
      className={`rounded-2xl border border-slate-200 bg-white ${compact ? "p-6" : "p-8"}`}
    >
      <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Field Notes · Roughly bi-monthly</div>
      <h3 className={`mt-2 font-semibold tracking-tight text-slate-900 ${compact ? "text-xl" : "text-2xl"}`}>
        Get short essays on cash & constraints.
      </h3>
      <p className="mt-2 text-[14.5px] text-slate-600 leading-relaxed max-w-lg">
        Written by {`\u200B`}Vivek Kashyap. No fluff, no promotions — just the trade-offs behind the numbers.
      </p>
      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          data-testid="newsletter-email"
          className="flex-1"
          required
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit"
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-slate-900 text-white text-sm font-semibold px-5 py-2 hover:bg-black disabled:opacity-60 whitespace-nowrap"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Subscribe
        </button>
      </form>
      <div className="mt-3 text-[11px] text-slate-500">Unsubscribe anytime. We never share your email.</div>
    </div>
  );
}
